import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  invoiceApi,
  productApi,
  productCategoryApi,
  inventoryItemDtlApi,
  vendorApi,
  stockReceiptApi,
} from "../api/api";
import { subscribeDataMutation } from "../utils/appEvents";
import "./common.css";

/* ── Status Color & Label Config ── */
const STATUS_CONFIG = {
  Draft: { color: "draft", label: "Draft", bg: "rgba(185,167,146,.15)", text: "#9e8872" },
  Finalised: { color: "finalised", label: "Finalised", bg: "rgba(39,174,96,.12)", text: "#27ae60" },
  Cancelled: { color: "cancelled", label: "Cancelled", bg: "rgba(192,57,43,.1)", text: "#c0392b" },
};

/* ── Curated Palette for Category Donut Chart ── */
const PALETTE = [
  "#C5A880", // Warm Gold
  "#D99B82", // Rose Gold / Blush
  "#8E7D6B", // Deep Taupe
  "#4A4444", // Charcoal Noir
  "#5C8D75", // Emerald Green
  "#5B7FA8", // Sapphire Blue
  "#C28CAE", // Amethyst Violet
  "#E0B05C", // Amber Gold
];

export default function Dashboard({ onNavigate }) {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [stockReceipts, setStockReceipts] = useState([]);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [hoveredSlice, setHoveredSlice] = useState(null);

  const lastFetchedRef = useRef(Date.now());

  const fetchDashboardData = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const [invData, prodData, catData, itemData, vendData, receiptData] = await Promise.all([
        invoiceApi.getAll().catch(() => []),
        productApi.getAll().catch(() => []),
        productCategoryApi.getAll().catch(() => []),
        inventoryItemDtlApi.getAll().catch(() => []),
        vendorApi.getAll().catch(() => []),
        stockReceiptApi.getAll().catch(() => []),
      ]);

      setInvoices(Array.isArray(invData) ? invData : invData?.data ?? []);
      setProducts(Array.isArray(prodData) ? prodData : prodData?.data ?? []);
      setCategories(Array.isArray(catData) ? catData : catData?.data ?? []);
      setInventoryItems(Array.isArray(itemData) ? itemData : itemData?.data ?? []);
      setVendors(Array.isArray(vendData) ? vendData : vendData?.data ?? []);
      setStockReceipts(Array.isArray(receiptData) ? receiptData : receiptData?.data ?? []);
      lastFetchedRef.current = Date.now();
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 1. Initial fetch
    fetchDashboardData(false);

    // 2. Real-time Cross-tab & In-app Mutation Listener (Instant update across split browser tabs)
    const unsubscribe = subscribeDataMutation((mutation) => {
      fetchDashboardData(true);
    });

    // 3. Tab Focus Revalidation (Refetch when window/tab is focused if idle > 5 seconds)
    const handleFocus = () => {
      const timeSinceLastFetch = Date.now() - lastFetchedRef.current;
      if (timeSinceLastFetch > 5000) {
        fetchDashboardData(true);
      }
    };
    window.addEventListener("focus", handleFocus);

    return () => {
      unsubscribe();
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchDashboardData]);

  /* ── Format helpers ── */
  const fmtINR = (v) =>
    v != null
      ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "₹0.00";

  const fmtShortINR = (v) => {
    const num = Number(v) || 0;
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    if (num >= 1000) return `₹${(num / 1000).toFixed(1)} k`;
    return `₹${num.toFixed(0)}`;
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /* ── Key Metrics Computations ── */
  const metrics = useMemo(() => {
    const finalisedInvoices = invoices.filter((i) => i.invoiceStatus === "Finalised");
    const draftInvoices = invoices.filter((i) => i.invoiceStatus === "Draft");
    const cancelledInvoices = invoices.filter((i) => i.invoiceStatus === "Cancelled");

    const totalRevenue = finalisedInvoices.reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);
    const draftAmount = draftInvoices.reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);

    const totalStockQty = inventoryItems.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
    const totalNetWeight = inventoryItems.reduce(
      (sum, item) => sum + (Number(item.netWeight) || Number(item.grossWeight) || 0),
      0
    );

    const avgInvoiceValue =
      finalisedInvoices.length > 0 ? totalRevenue / finalisedInvoices.length : 0;

    return {
      totalRevenue,
      finalisedCount: finalisedInvoices.length,
      draftCount: draftInvoices.length,
      draftAmount,
      cancelledCount: cancelledInvoices.length,
      totalInvoices: invoices.length,
      totalStockQty,
      totalNetWeight,
      totalProducts: products.length,
      totalVendors: vendors.length,
      totalReceipts: stockReceipts.length,
      avgInvoiceValue,
    };
  }, [invoices, inventoryItems, products, vendors, stockReceipts]);

  /* ── Monthly Revenue (Last 6 Months) ── */
  const monthlyData = useMemo(() => {
    const monthsMap = new Map();
    const now = new Date();

    // Generate last 6 months buckets
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
      monthsMap.set(key, { key, label, revenue: 0, count: 0 });
    }

    // Populate with finalised invoices
    invoices.forEach((inv) => {
      if (inv.invoiceStatus === "Finalised" && inv.invoiceDate) {
        const invDate = new Date(inv.invoiceDate);
        if (!isNaN(invDate.getTime())) {
          const key = `${invDate.getFullYear()}-${String(invDate.getMonth() + 1).padStart(2, "0")}`;
          if (monthsMap.has(key)) {
            const entry = monthsMap.get(key);
            entry.revenue += Number(inv.grossAmount) || 0;
            entry.count += 1;
          }
        }
      }
    });

    const data = Array.from(monthsMap.values());
    const maxRev = Math.max(...data.map((d) => d.revenue), 1000);
    return { data, maxRev };
  }, [invoices]);

  /* ── Category Breakdown (Donut Chart) ── */
  const categoryBreakdown = useMemo(() => {
    // 1. Build fast lookup maps supporting numeric & string IDs and both camel/Pascal case
    const catMap = new Map();
    (categories || []).forEach((c) => {
      const id = c.categoryID ?? c.CategoryID;
      const name = (c.categoryName ?? c.CategoryName ?? "").trim();
      if (id != null && name) {
        catMap.set(Number(id), name);
        catMap.set(String(id), name);
      }
    });

    const prodMap = new Map();
    (products || []).forEach((p) => {
      const id = p.productID ?? p.ProductID;
      if (id != null) {
        prodMap.set(Number(id), p);
        prodMap.set(String(id), p);
      }
    });

    // Helper to resolve the best Category Name for a product or item
    const resolveCategoryName = (prodId, fallbackItem) => {
      const p = prodId != null ? (prodMap.get(Number(prodId)) || prodMap.get(String(prodId))) : null;
      const catId = p ? (p.categoryID ?? p.CategoryID) : (fallbackItem?.categoryID ?? fallbackItem?.CategoryID);

      // 1. Direct Category ID match
      if (catId != null && catId !== 0) {
        const found = catMap.get(Number(catId)) || catMap.get(String(catId));
        if (found) return found;
      }

      // 2. Direct categoryName property on ProductDTO if provided by backend
      if (p?.categoryName || p?.CategoryName) {
        return (p.categoryName || p.CategoryName).trim();
      }

      // 3. Match against known categories list by product name
      const pName = (p?.productName || p?.ProductName || "").toLowerCase();
      if (pName && categories.length > 0) {
        const matched = categories.find((c) => {
          const cName = (c.categoryName || c.CategoryName || "").toLowerCase().trim();
          return cName && (pName.includes(cName) || cName.includes(pName));
        });
        if (matched) return (matched.categoryName || matched.CategoryName).trim();
      }

      // 4. Use the product's actual name rather than a static placeholder
      if (p?.productName || p?.ProductName) {
        return (p.productName || p.ProductName).trim();
      }

      // 5. If system has categories, assign to the first active category
      if (categories.length > 0) {
        const firstCat = (categories[0].categoryName || categories[0].CategoryName || "").trim();
        if (firstCat) return firstCat;
      }

      return "Category";
    };

    const counts = new Map();
    let totalAssigned = 0;

    // Aggregate by items or products
    if (inventoryItems && inventoryItems.length > 0) {
      inventoryItems.forEach((item) => {
        const pId = item.productID ?? item.ProductID;
        const name = resolveCategoryName(pId, item);
        const qty = Number(item.quantity) || 1;
        counts.set(name, (counts.get(name) || 0) + qty);
        totalAssigned += qty;
      });
    } else if (products && products.length > 0) {
      products.forEach((prod) => {
        const pId = prod.productID ?? prod.ProductID;
        const name = resolveCategoryName(pId, prod);
        counts.set(name, (counts.get(name) || 0) + 1);
        totalAssigned += 1;
      });
    } else if (categories && categories.length > 0) {
      // If no inventory items or products yet, display available categories
      categories.forEach((cat) => {
        const name = (cat.categoryName || cat.CategoryName || "").trim();
        if (name) {
          counts.set(name, 1);
          totalAssigned += 1;
        }
      });
    }

    const sorted = Array.from(counts.entries())
      .map(([name, count], index) => ({
        name,
        count,
        pct: totalAssigned > 0 ? (count / totalAssigned) * 100 : 0,
        color: PALETTE[index % PALETTE.length],
      }))
      .sort((a, b) => b.count - a.count);

    // Keep top 5 and group remainder into "Other" if too many
    if (sorted.length > 5) {
      const top = sorted.slice(0, 4);
      const rest = sorted.slice(4);
      const otherCount = rest.reduce((s, r) => s + r.count, 0);
      const otherPct = rest.reduce((s, r) => s + r.pct, 0);
      top.push({
        name: "Other Categories",
        count: otherCount,
        pct: otherPct,
        color: "#B9A792",
      });
      return { items: top, total: totalAssigned };
    }

    return { items: sorted, total: totalAssigned };
  }, [categories, products, inventoryItems]);

  /* ── Recent 5 Invoices ── */
  const recentInvoices = useMemo(() => {
    return [...invoices]
      .sort((a, b) => {
        const da = a.invoiceDate ? new Date(a.invoiceDate).getTime() : 0;
        const db = b.invoiceDate ? new Date(b.invoiceDate).getTime() : 0;
        return db - da || b.invoiceID - a.invoiceID;
      })
      .slice(0, 5);
  }, [invoices]);

  /* ── Recent Stock Deliveries ── */
  const recentReceipts = useMemo(() => {
    const vMap = Object.fromEntries(vendors.map((v) => [v.vendorID, v.vendorName]));
    return [...stockReceipts]
      .sort((a, b) => {
        const da = a.receiptDate ? new Date(a.receiptDate).getTime() : 0;
        const db = b.receiptDate ? new Date(b.receiptDate).getTime() : 0;
        return db - da || b.receiptID - a.receiptID;
      })
      .slice(0, 4)
      .map((r) => ({
        ...r,
        vendorName: vMap[r.vendorID] || `Vendor #${r.vendorID}`,
      }));
  }, [stockReceipts, vendors]);

  return (
    <div className="rv-dashboard-container" style={{ paddingBottom: 30 }}>
      {/* ── Top Header Banner ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: "var(--d-font)",
              fontSize: 20,
              fontWeight: 600,
              color: "var(--dark)",
              marginBottom: 4,
            }}
          >
            Store Overview & Analytics
          </h2>
          <p style={{ fontSize: 13, color: "var(--taupe-dark)" }}>
            Live performance metrics, inventory health, and recent store activities.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            className="rv-btn rv-btn--ghost"
            style={{ background: "#fff", borderColor: "var(--border)", padding: "8px 14px", fontSize: 12.5 }}
            onClick={fetchDashboardData}
            title="Refresh Live Data"
            disabled={loading}
          >
            <i className={`bi bi-arrow-clockwise ${loading ? "rv-spin-icon" : ""}`} style={{ marginRight: 6 }} />
            {loading ? "Refreshing…" : "Refresh"}
          </button>

          {onNavigate && (
            <button
              className="rv-btn rv-btn--primary"
              style={{ padding: "8px 16px", fontSize: 12.5 }}
              onClick={() => onNavigate("Invoice")}
            >
              <i className="bi bi-plus-lg" style={{ marginRight: 6 }} />
              New Invoice
            </button>
          )}
        </div>
      </div>

      {/* ── Row 1: KPI Summary Cards ── */}
      <div className="row g-3 mb-4">
        {/* Card 1: Revenue */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="rv-card rv-stat-card"
            style={{
              padding: "20px 22px",
              background: "linear-gradient(135deg, #FFFFFF 0%, rgba(244,223,214,.35) 100%)",
              border: "1px solid rgba(185,167,146,.25)",
              borderRadius: "var(--rad)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--taupe-dark)" }}>
                Total Revenue
              </span>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "rgba(39,174,96,.12)",
                  color: "#27ae60",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                }}
              >
                <i className="bi bi-currency-rupee" />
              </div>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "var(--dark)", marginBottom: 6 }}>
              {fmtINR(metrics.totalRevenue)}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--taupe-dark)", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "#27ae60", fontWeight: 600 }}>{metrics.finalisedCount} Finalised</span>
              <span>·</span>
              <span>Avg: {fmtShortINR(metrics.avgInvoiceValue)}</span>
            </div>
          </div>
        </div>

        {/* Card 2: Invoices Breakdown */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="rv-card rv-stat-card"
            style={{
              padding: "20px 22px",
              background: "#fff",
              border: "1px solid rgba(185,167,146,.25)",
              borderRadius: "var(--rad)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--taupe-dark)" }}>
                Total Invoices
              </span>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "rgba(185,167,146,.15)",
                  color: "var(--dark)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                }}
              >
                <i className="bi bi-receipt" />
              </div>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "var(--dark)", marginBottom: 6 }}>
              {metrics.totalInvoices}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--taupe-dark)", display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span style={{ color: "#9e8872", fontWeight: 600 }}>{metrics.draftCount} Drafts</span>
              <span>·</span>
              <span style={{ color: "#c0392b" }}>{metrics.cancelledCount} Cancelled</span>
            </div>
          </div>
        </div>

        {/* Card 3: Physical Stock Inventory */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="rv-card rv-stat-card"
            style={{
              padding: "20px 22px",
              background: "#fff",
              border: "1px solid rgba(185,167,146,.25)",
              borderRadius: "var(--rad)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--taupe-dark)" }}>
                Jewellery in Stock
              </span>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "rgba(92,141,117,.12)",
                  color: "#3d7a68",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                }}
              >
                <i className="bi bi-gem" />
              </div>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "var(--dark)", marginBottom: 6 }}>
              {metrics.totalStockQty}{" "}
              <span style={{ fontSize: 14, fontWeight: 500, color: "var(--taupe-dark)" }}>pieces</span>
            </div>
            <div style={{ fontSize: 11.5, color: "var(--taupe-dark)" }}>
              Net Wt: <strong style={{ color: "var(--dark)" }}>{metrics.totalNetWeight.toFixed(3)} g</strong>
            </div>
          </div>
        </div>

        {/* Card 4: Catalog & Partners */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div
            className="rv-card rv-stat-card"
            style={{
              padding: "20px 22px",
              background: "#fff",
              border: "1px solid rgba(185,167,146,.25)",
              borderRadius: "var(--rad)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--taupe-dark)" }}>
                Catalog & Vendors
              </span>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "rgba(91,127,168,.12)",
                  color: "#4a6fa5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                }}
              >
                <i className="bi bi-boxes" />
              </div>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "var(--dark)", marginBottom: 6 }}>
              {metrics.totalProducts}{" "}
              <span style={{ fontSize: 14, fontWeight: 500, color: "var(--taupe-dark)" }}>products</span>
            </div>
            <div style={{ fontSize: 11.5, color: "var(--taupe-dark)", display: "flex", gap: 6 }}>
              <span>{metrics.totalVendors} Vendors</span>
              <span>·</span>
              <span>{metrics.totalReceipts} Stock Receipts</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 2: Charts Row ── */}
      <div className="row g-3 mb-4">
        {/* Monthly Revenue Bar Chart */}
        <div className="col-12 col-lg-7">
          <div
            className="rv-card h-100"
            style={{
              padding: "22px 24px",
              background: "#fff",
              border: "1px solid var(--border)",
              borderRadius: "var(--rad)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div>
                <h3 style={{ fontFamily: "var(--d-font)", fontSize: 15, fontWeight: 600, color: "var(--dark)", marginBottom: 2 }}>
                  Monthly Revenue Trend
                </h3>
                <p style={{ fontSize: 11.5, color: "var(--taupe-dark)" }}>
                  Finalised sales revenue across recent months
                </p>
              </div>
              <span className="rv-invitem-id-chip" style={{ fontSize: 11 }}>
                Last 6 Months
              </span>
            </div>

            {/* SVG Bar Chart */}
            <div style={{ flex: 1, minHeight: 220, position: "relative", display: "flex", flexDirection: "column" }}>
              <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 16, padding: "10px 10px 0 10px" }}>
                {monthlyData.data.map((m, idx) => {
                  const heightPct = Math.max((m.revenue / monthlyData.maxRev) * 100, 4);
                  const isHovered = hoveredBar === idx;

                  return (
                    <div
                      key={m.key}
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        height: "100%",
                        justifyContent: "flex-end",
                        position: "relative",
                        cursor: "pointer",
                      }}
                      onMouseEnter={() => setHoveredBar(idx)}
                      onMouseLeave={() => setHoveredBar(null)}
                    >
                      {/* Floating Tooltip */}
                      {isHovered && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: `${Math.min(heightPct + 12, 90)}%`,
                            background: "var(--dark)",
                            color: "#fff",
                            padding: "6px 10px",
                            borderRadius: 8,
                            fontSize: 11,
                            whiteSpace: "nowrap",
                            boxShadow: "0 4px 14px rgba(0,0,0,.2)",
                            zIndex: 10,
                            pointerEvents: "none",
                            textAlign: "center",
                          }}
                        >
                          <div style={{ fontWeight: 700 }}>{fmtINR(m.revenue)}</div>
                          <div style={{ fontSize: 10, color: "rgba(255,255,255,.75)" }}>{m.count} invoice(s)</div>
                        </div>
                      )}

                      {/* Bar fill */}
                      <div
                        style={{
                          width: "100%",
                          maxWidth: 44,
                          height: `${heightPct}%`,
                          background: isHovered
                            ? "linear-gradient(180deg, #B9A792 0%, #9E8872 100%)"
                            : "linear-gradient(180deg, #E8CAB8 0%, #B9A792 100%)",
                          borderRadius: "6px 6px 0 0",
                          transition: "height .3s ease, background .2s ease, transform .2s ease",
                          transform: isHovered ? "scaleY(1.03)" : "scaleY(1)",
                          transformOrigin: "bottom",
                          boxShadow: isHovered ? "0 4px 12px rgba(185,167,146,.4)" : "none",
                        }}
                      />

                      {/* Month label */}
                      <span
                        style={{
                          marginTop: 8,
                          fontSize: 11,
                          fontWeight: isHovered ? 700 : 500,
                          color: isHovered ? "var(--dark)" : "var(--taupe-dark)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Baseline border */}
              <div style={{ height: 1, background: "rgba(185,167,146,.2)", marginTop: 4 }} />
            </div>
          </div>
        </div>

        {/* Category Share Donut Chart */}
        <div className="col-12 col-lg-5">
          <div
            className="rv-card h-100"
            style={{
              padding: "22px 24px",
              background: "#fff",
              border: "1px solid var(--border)",
              borderRadius: "var(--rad)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ marginBottom: 14 }}>
              <h3 style={{ fontFamily: "var(--d-font)", fontSize: 15, fontWeight: 600, color: "var(--dark)", marginBottom: 2 }}>
                Category Distribution
              </h3>
              <p style={{ fontSize: 11.5, color: "var(--taupe-dark)" }}>
                Stock & product allocation by category
              </p>
            </div>

            {categoryBreakdown.items.length === 0 ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--taupe)" }}>
                No category data available
              </div>
            ) : (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 16 }}>
                {/* SVG Donut Graphic */}
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", position: "relative" }}>
                  <svg width="170" height="170" viewBox="0 0 100 100" style={{ transform: "rotate(-90deg)" }}>
                    {(() => {
                      let accumulatedPct = 0;
                      return categoryBreakdown.items.map((item, idx) => {
                        const strokeDasharray = `${item.pct} ${100 - item.pct}`;
                        const strokeDashoffset = -accumulatedPct;
                        accumulatedPct += item.pct;
                        const isHovered = hoveredSlice === idx;

                        return (
                          <circle
                            key={item.name}
                            cx="50"
                            cy="50"
                            r="38"
                            fill="transparent"
                            stroke={item.color}
                            strokeWidth={isHovered ? "16" : "12"}
                            strokeDasharray={strokeDasharray}
                            strokeDashoffset={strokeDashoffset}
                            pathLength="100"
                            style={{
                              transition: "stroke-width .2s ease, opacity .2s ease",
                              opacity: hoveredSlice !== null && !isHovered ? 0.6 : 1,
                              cursor: "pointer",
                            }}
                            onMouseEnter={() => setHoveredSlice(idx)}
                            onMouseLeave={() => setHoveredSlice(null)}
                          />
                        );
                      });
                    })()}
                  </svg>

                  {/* Inner text inside Donut */}
                  <div
                    style={{
                      position: "absolute",
                      textAlign: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--dark)" }}>
                      {hoveredSlice !== null
                        ? `${categoryBreakdown.items[hoveredSlice].pct.toFixed(0)}%`
                        : `${categoryBreakdown.total}`}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--taupe-dark)", textTransform: "uppercase", letterSpacing: ".5px" }}>
                      {hoveredSlice !== null ? categoryBreakdown.items[hoveredSlice].name : "Total Items"}
                    </div>
                  </div>
                </div>

                {/* Legend list */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 14px", fontSize: 11.5 }}>
                  {categoryBreakdown.items.map((item, idx) => (
                    <div
                      key={item.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        cursor: "pointer",
                        opacity: hoveredSlice !== null && hoveredSlice !== idx ? 0.5 : 1,
                        transition: "opacity .2s",
                      }}
                      onMouseEnter={() => setHoveredSlice(idx)}
                      onMouseLeave={() => setHoveredSlice(null)}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 3,
                          background: item.color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ color: "var(--dark)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.name}
                      </span>
                      <span style={{ marginLeft: "auto", color: "var(--taupe-dark)", fontWeight: 600 }}>
                        {item.pct.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Row 3: Recent Invoices & Quick Actions ── */}
      <div className="row g-3">
        {/* Left: Recent Invoices Table */}
        <div className="col-12 col-lg-8">
          <div
            className="rv-card"
            style={{
              padding: "22px 24px",
              background: "#fff",
              border: "1px solid var(--border)",
              borderRadius: "var(--rad)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <h3 style={{ fontFamily: "var(--d-font)", fontSize: 15, fontWeight: 600, color: "var(--dark)", marginBottom: 2 }}>
                  Recent Invoices
                </h3>
                <p style={{ fontSize: 11.5, color: "var(--taupe-dark)" }}>
                  Latest customer billing transactions
                </p>
              </div>

              {onNavigate && (
                <button
                  className="rv-link"
                  style={{ fontSize: 12.5, fontWeight: 600, color: "var(--taupe-dark)", display: "flex", alignItems: "center", gap: 4 }}
                  onClick={() => onNavigate("Invoice")}
                >
                  View all invoices <i className="bi bi-arrow-right" />
                </button>
              )}
            </div>

            {recentInvoices.length === 0 ? (
              <div style={{ padding: "30px 0", textAlign: "center", color: "var(--taupe)" }}>
                No invoices recorded yet.
              </div>
            ) : (
              <div className="rv-table-wrap">
                <table className="rv-table" style={{ fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      <th>Invoice No.</th>
                      <th>Customer</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Gross Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentInvoices.map((inv) => {
                      const st = STATUS_CONFIG[inv.invoiceStatus] || STATUS_CONFIG.Draft;
                      return (
                        <tr
                          key={inv.invoiceID}
                          className="rv-txn-row"
                          style={{ cursor: "pointer" }}
                          onClick={() => onNavigate && onNavigate("Invoice")}
                        >
                          <td>
                            <span className="rv-txn-id-badge">{inv.invoiceNumber}</span>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, color: "var(--dark)" }}>{inv.customerName || "—"}</div>
                            {inv.customerPhone && (
                              <div style={{ fontSize: 10.5, color: "var(--taupe-dark)" }}>{inv.customerPhone}</div>
                            )}
                          </td>
                          <td>{fmtDate(inv.invoiceDate)}</td>
                          <td>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "2px 8px",
                                borderRadius: 12,
                                fontSize: 11,
                                fontWeight: 600,
                                background: st.bg,
                                color: st.text,
                              }}
                            >
                              {inv.invoiceStatus}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <strong>{fmtINR(inv.grossAmount)}</strong>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right: Quick Action Shortcuts & Vendor Receipts */}
        <div className="col-12 col-lg-4">
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Quick Actions Card */}
            <div
              className="rv-card"
              style={{
                padding: "20px 22px",
                background: "#fff",
                border: "1px solid var(--border)",
                borderRadius: "var(--rad)",
              }}
            >
              <h3 style={{ fontFamily: "var(--d-font)", fontSize: 14, fontWeight: 600, color: "var(--dark)", marginBottom: 12 }}>
                Quick Shortcuts
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[
                  { label: "New Invoice", icon: "bi-receipt", page: "Invoice" },
                  { label: "Add Product", icon: "bi-box-seam", page: "Products" },
                  { label: "Stock Delivery", icon: "bi-truck", page: "Stock Receipt" },
                  { label: "View Stocks", icon: "bi-stack", page: "Stocks" },
                ].map((act) => (
                  <button
                    key={act.label}
                    className="rv-btn rv-btn--ghost"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                      padding: "12px 8px",
                      borderRadius: 10,
                      border: "1px solid rgba(185,167,146,.2)",
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "var(--dark)",
                      background: "rgba(185,167,146,.04)",
                      textAlign: "center",
                    }}
                    onClick={() => onNavigate && onNavigate(act.page)}
                  >
                    <i className={`bi ${act.icon}`} style={{ fontSize: 16, color: "var(--taupe-dark)" }} />
                    {act.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Deliveries */}
            <div
              className="rv-card"
              style={{
                padding: "20px 22px",
                background: "#fff",
                border: "1px solid var(--border)",
                borderRadius: "var(--rad)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ fontFamily: "var(--d-font)", fontSize: 14, fontWeight: 600, color: "var(--dark)", marginBottom: 0 }}>
                  Recent Deliveries
                </h3>
                {onNavigate && (
                  <button
                    className="rv-link"
                    style={{ fontSize: 11.5, color: "var(--taupe-dark)" }}
                    onClick={() => onNavigate("Stock Receipt")}
                  >
                    View all
                  </button>
                )}
              </div>

              {recentReceipts.length === 0 ? (
                <p style={{ fontSize: 12, color: "var(--taupe)", textAlign: "center", margin: "14px 0" }}>
                  No vendor deliveries recorded.
                </p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {recentReceipts.map((r) => (
                    <div
                      key={r.receiptID}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 10px",
                        background: "rgba(185,167,146,.06)",
                        borderRadius: 8,
                        cursor: "pointer",
                      }}
                      onClick={() => onNavigate && onNavigate("Stock Receipt")}
                    >
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--dark)" }}>
                          Receipt #{r.receiptID}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--taupe-dark)" }}>{r.vendorName}</div>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--taupe)", textAlign: "right" }}>
                        {fmtDate(r.receiptDate)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
